/* eslint-disable react/prop-types */
import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency } from 'utils/CommonFunction';
import { MerchantStationRevenueColor } from 'utils/StaticData';

// Defined outside component so reference is stable — prevents recharts tooltip unmount/remount
const StationTooltip = ({ active, payload, label, dynamicData, currency }) => {
  if (!active || !payload?.length) return null;

  const header = dynamicData?.length === 1 ? dynamicData[0].merchant : label;

  return (
    <Box
      bg="white"
      p={3}
      border="1px solid #ddd"
      borderRadius="md"
      boxShadow="md"
    >
      <Text fontWeight="bold" mb={2}>
        {header}
      </Text>
      {payload.map((entry, index) => {
        const stationId = entry.name.replace(' (Revenue)', '');
        const revenue = entry.value;
        const count = entry.payload[`${stationId}-count`];

        return (
          <Box key={index} fontSize="sm" mb={2}>
            <Text>
              <strong>Station ID:</strong> {stationId}
            </Text>
            <Text>
              <strong>Revenue:</strong> {formatCurrency(revenue, currency)}
            </Text>
            <Text>
              <strong>Count:</strong> {count}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
};

const MerchantRevenueStationGraph = ({
  dynamicData,
  isRecurringLoading,
  currency,
}) => {
  const COLORS = MerchantStationRevenueColor;

  const allStationIds = useMemo(
    () =>
      Array.from(
        new Set(
          dynamicData?.flatMap((merchant) =>
            merchant.stations.map((s) => s.stationId),
          ),
        ),
      ),
    [dynamicData],
  );

  const transformedData = useMemo(
    () =>
      dynamicData?.length === 1
        ? dynamicData[0].stations.map((station) => ({
            name: station.stationId,
            [station.stationId]: station.revenue,
            [`${station.stationId}-count`]: station.count,
          }))
        : dynamicData?.map((merchant) => ({
            name: merchant.merchant,
            ...merchant.stations.reduce(
              (acc, station) => ({
                ...acc,
                [station.stationId]: station.revenue,
                [`${station.stationId}-count`]: station.count,
              }),
              {},
            ),
          })),
    [dynamicData],
  );

  if (isRecurringLoading) {
    return (
      <Flex justify="center" align="center" height="300px" width="100%">
        <Spinner size="lg" color="black.500" />
      </Flex>
    );
  }

  if (dynamicData?.length === 0) {
    return (
      <Flex justify="center" alignItems="center" height="300px">
        <Text fontSize="18px" fontWeight="bold" color="gray.500">
          No records available
        </Text>
      </Flex>
    );
  }

  const barBottomMargin = transformedData?.length < 4 ? -60 : 0;

  return (
    <Box width="100%" overflowX="auto">
      {/* Dynamic width container */}
      <Box
        minWidth={`${Math.max(transformedData?.length * 50, 600)}px`}
        height={300}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={transformedData}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: barBottomMargin,
            }}
            barCategoryGap={transformedData?.length < 5 ? '10%' : '20%'}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="name"
              interval={0}
              tick={(props) => {
                const { x, y, payload } = props;
                const name = payload.value;
                const maxLength = 15;
                const truncatedName =
                  transformedData?.length > 4
                    ? `${name.substring(0, maxLength)}…`
                    : name;

                return (
                  <text
                    x={x}
                    y={y + 10}
                    textAnchor={transformedData?.length < 4 ? 'middle' : 'end'}
                    fontSize={12}
                    transform={
                      transformedData?.length < 4
                        ? undefined
                        : `rotate(-60, ${x}, ${y})`
                    }
                  >
                    {truncatedName}
                  </text>
                );
              }}
              height={120}
            />
            <YAxis
              tickFormatter={(value) => `${formatCurrency(value, currency)}`}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              content={
                <StationTooltip dynamicData={dynamicData} currency={currency} />
              }
            />

            {/* Revenue Bars */}
            {allStationIds.map((stationId) => {
              let index = 0;

              if (dynamicData?.length > 1) {
                dynamicData.forEach((merchant) => {
                  const indexC = merchant.stations.findIndex(
                    (station) => station.stationId === stationId,
                  );
                  if (indexC !== -1) {
                    index = indexC;
                  }
                });
              }

              return (
                <Bar
                  key={`${stationId}-revenue`}
                  dataKey={stationId}
                  stackId="a"
                  fill={COLORS[index % COLORS.length]}
                  name={stationId}
                  shape={(props) => {
                    const { x, y, width, height } = props;
                    const stationRevenue = props.payload[stationId];

                    if (height === 0 || stationRevenue === 0) {
                      return null;
                    }

                    const allPositive = allStationIds.filter(
                      (id) => props.payload[id] > 0,
                    );
                    const isTopBar =
                      stationId === allPositive[allPositive.length - 1];

                    if (isTopBar) {
                      const radius = 5;
                      const right = x + width;
                      const bottom = y + height;

                      return (
                        <path
                          d={`
                            M ${x},${bottom}
                            L ${x},${y + radius}
                            Q ${x},${y} ${x + radius},${y}
                            L ${right - radius},${y}
                            Q ${right},${y} ${right},${y + radius}
                            L ${right},${bottom}
                            Z
                          `}
                          fill={COLORS[index % COLORS.length]}
                        />
                      );
                    }

                    // Default rect for non-top bars
                    return (
                      <rect
                        x={x}
                        y={y}
                        width={width}
                        height={height}
                        fill={COLORS[index % COLORS.length]}
                      />
                    );
                  }}
                />
              );
            })}
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default React.memo(MerchantRevenueStationGraph);
